import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, toast } from "@aurelia/ui";
import { useRegister, useGoogleAuth } from "../../features/auth/mutations";
import { useAuthStore } from "../../lib/stores/auth";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { GoogleSignInButton } from "../../components/auth/GoogleSignInButton";

export function RegisterPage() {
  useDocumentMeta("Create Account", "Create a Latif General Store account to track orders and earn reward coins.");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const register = useRegister();
  const googleAuth = useGoogleAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await register.mutateAsync({ phone, password, fullName, email: email || undefined });
    navigate("/account");
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      const result = await googleAuth.mutateAsync(idToken);
      if (result.status === "success") {
        setUser(result.user);
        queryClient.setQueryData(["me"], { user: result.user });
        navigate("/account");
        return;
      }
      // Only staff accounts ever hit a PIN step — send them to the login
      // page's full PIN flow rather than duplicating it here.
      navigate("/login");
    } catch (err) {
      toast((err as Error).message ?? "Google sign-in failed", "danger");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-canvas px-6">
      <form onSubmit={handleSubmit} className="w-full max-w-sm flex flex-col gap-5">
        <h1 className="text-display-sm text-center mb-2">Create your account</h1>
        <Input
          label="Full name"
          name="fullName"
          autoComplete="name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <Input
          label="Phone number"
          type="tel"
          name="phone"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Input
          label="Email (optional)"
          type="email"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {register.isError && (
          <p role="alert" className="text-sm text-red-500">
            {(register.error as Error).message}
          </p>
        )}
        <Button type="submit" size="lg" disabled={register.isPending} className="w-full">
          {register.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {register.isPending ? "Creating account…" : "Create account"}
        </Button>
        {register.isPending && (
          <p className="text-xs text-ink-muted text-center -mt-2">
            This can take a few seconds on a slow connection — please don't refresh.
          </p>
        )}
        <div className="flex items-center gap-3 -my-1">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-ink-muted">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <GoogleSignInButton onCredential={handleGoogleCredential} />
        <p className="text-sm text-ink-muted text-center">
          Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
        </p>
      </form>
    </main>
  );
}
